import { useContext, useEffect, useMemo, useState } from "react";
import {
  FiCheck,
  FiHeart,
  FiMinus,
  FiPlus,
  FiShoppingBag,
  FiStar,
} from "react-icons/fi";
import { Link, useParams } from "react-router-dom";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/productdetails.scss";
import { productImageSrcSet } from "../utils/images";
import { shippingSummary } from "../utils/shipping";
import { CartContext } from "./CartContext";
import PageMeta from "./PageMeta";
import ProductCard, { money } from "./ProductCard";

const mediaTones = [
  "#e9bc4f",
  "#bd6062",
  "#8da08a",
  "#d2a17b",
  "#7d9ca4",
  "#b49a72",
];

const safeDecode = (value) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const attributeLabel = (value) =>
  String(value)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const normalizeFormats = (product) => {
  const rawFormats = Array.isArray(product?.formats)
    ? product.formats
    : [product?.unit || "Standard format"];

  return rawFormats.filter(Boolean).map((format, index) => {
    if (typeof format === "string" || typeof format === "number") {
      return {
        id: String(format),
        label: String(format),
        price: Number(product?.price),
      };
    }

    const label = format.label || format.name || format.unit || `Format ${index + 1}`;
    return {
      id: String(format.id || format.value || label),
      label,
      price: Number.isFinite(Number(format.price))
        ? Number(format.price)
        : Number(product?.price),
    };
  });
};

function ProductDetail() {
  const params = useParams();
  const identifier = params.slug ?? params.id ?? "";
  const { addToCart, isInWishlist, toggleWishlist } = useContext(CartContext);

  const product = useMemo(() => {
    const normalizedIdentifier = safeDecode(String(identifier)).trim().toLowerCase();

    return productData.find(
      (item) =>
        String(item.id).toLowerCase() === normalizedIdentifier ||
        String(item.slug || "").toLowerCase() === normalizedIdentifier
    );
  }, [identifier]);

  const images = useMemo(() => {
    if (!product) return [];
    if (Array.isArray(product.images)) return product.images.filter(Boolean);
    return product.image ? [product.image] : [];
  }, [product]);

  const formats = useMemo(() => normalizeFormats(product), [product]);
  const attributeEntries = useMemo(
    () =>
      Object.entries(product?.attributes || {}).filter(
        ([key, value]) => key.toLowerCase() !== "ingredients" && value
      ),
    [product]
  );

  const relatedProducts = useMemo(() => {
    if (!product) return [];

    const sameCategory = productData.filter(
      (item) => item.id !== product.id && item.category === product.category
    );
    const remaining = productData.filter(
      (item) =>
        item.id !== product.id &&
        !sameCategory.some((related) => related.id === item.id)
    );

    return [...sameCategory, ...remaining].slice(0, 3);
  }, [product]);

  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedFormat, setSelectedFormat] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    setSelectedImage(0);
    setSelectedFormat(formats[0]?.id || "");
    setQuantity(1);
    setAdded(false);
  }, [formats, product?.id]);

  useEffect(() => {
    if (!added) return undefined;
    const timer = window.setTimeout(() => setAdded(false), 3500);
    return () => window.clearTimeout(timer);
  }, [added]);

  if (!product) {
    return (
      <main className="product-detail-page product-detail-page--missing">
        <PageMeta title="Product not found" />
        <div className="product-detail__missing-card">
          <p className="product-detail__eyebrow">Our pantry</p>
          <h1>We could not find that product.</h1>
          <p>It may have moved, or it may no longer be part of this collection.</p>
          <Link className="product-detail__back-link" to="/shop">
            Explore all products
          </Link>
        </div>
      </main>
    );
  }

  const stock = Number(product.stock);
  const hasManagedStock = Number.isFinite(stock);
  const inStock = !hasManagedStock || stock > 0;
  const maxQuantity = hasManagedStock ? Math.max(1, Math.floor(stock)) : 99;
  const selectedFormatData =
    formats.find((format) => format.id === selectedFormat) || formats[0];
  const currentPrice = Number.isFinite(selectedFormatData?.price)
    ? selectedFormatData.price
    : Number(product.price) || 0;
  const wished = isInWishlist(product.id);
  const ingredients = product.attributes?.ingredients || "See packaging for ingredients.";
  const shipping = siteConfig.shipping || {};
  const mediaTone = mediaTones[(Number(product.id) - 1) % mediaTones.length];

  const updateQuantity = (nextQuantity) => {
    setQuantity(Math.min(maxQuantity, Math.max(1, nextQuantity)));
    setAdded(false);
  };

  const handleAddToCart = () => {
    if (!inStock) return;

    addToCart(
      { ...product, price: currentPrice },
      {
        quantity,
        variant: selectedFormat ? { format: selectedFormatData.label } : {},
      }
    );
    setAdded(true);
  };

  return (
    <main
      className="product-detail-page"
      itemScope
      itemType="https://schema.org/Product"
    >
      <PageMeta title={product.name} description={product.description} />
      <meta itemProp="sku" content={String(product.id)} />

      <header className="product-detail__heading">
        <div className="product-detail__breadcrumbs" aria-label="Breadcrumb">
          <Link to="/shop">Italian pantry</Link>
          <span aria-hidden="true">/</span>
          <Link to={`/shop?category=${encodeURIComponent(product.category)}`}>
            {product.category}
          </Link>
        </div>
        <h1 itemProp="name">{product.name}</h1>
        <p className="product-detail__subtitle" itemProp="description">
          {product.description}
        </p>
        <div className="product-detail__origin-line">
          <span>{product.attributes?.origin || "Sourced in Italy"}</span>
          <span aria-hidden="true">•</span>
          <span>{product.unit}</span>
        </div>
      </header>

      <section className="product-detail__purchase-grid" aria-label="Product options">
        <aside className="product-detail__specifications">
          {formats.length > 1 && (
            <section className="product-detail__spec-section">
              <h2>Formats</h2>
              <div className="product-detail__formats" role="group" aria-label="Choose a format">
                {formats.map((format) => (
                  <button
                    className={format.id === selectedFormat ? "is-active" : ""}
                    key={format.id}
                    type="button"
                    aria-pressed={format.id === selectedFormat}
                    onClick={() => {
                      setSelectedFormat(format.id);
                      setAdded(false);
                    }}
                  >
                    <span>{format.label}</span>
                    <span>{money.format(format.price)}</span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {attributeEntries.length > 0 && (
            <section className="product-detail__spec-section">
              <h2>At a glance</h2>
              <dl className="product-detail__attributes">
                {attributeEntries.map(([key, value]) => (
                  <div key={key}>
                    <dt>{attributeLabel(key)}</dt>
                    <dd>{Array.isArray(value) ? value.join(", ") : value}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          <section className="product-detail__spec-section product-detail__ingredients">
            <h2>Ingredients</h2>
            <p>{Array.isArray(ingredients) ? ingredients.join(", ") : ingredients}</p>
          </section>
        </aside>

        <div
          className="product-detail__media"
          style={{ "--product-tone": mediaTone }}
        >
          <div className="product-detail__image-stage">
            {images.length > 0 ? (
              <img
                src={images[selectedImage] || images[0]}
                srcSet={productImageSrcSet(images[selectedImage] || images[0])}
                sizes="(max-width: 760px) 92vw, 46vw"
                alt={`${product.name}${images.length > 1 ? `, view ${selectedImage + 1}` : ""}`}
                width="1200"
                height="1200"
                itemProp="image"
                loading="eager"
                decoding="async"
              />
            ) : (
              <div className="product-detail__image-placeholder">
                Product image coming soon
              </div>
            )}
          </div>

          {images.length > 1 && (
            <div className="product-detail__thumbnails" aria-label="Product images">
              {images.map((image, index) => (
                <button
                  className={index === selectedImage ? "is-active" : ""}
                  type="button"
                  key={`${image}-${index}`}
                  onClick={() => setSelectedImage(index)}
                  aria-label={`Show view ${index + 1} of ${product.name}`}
                  aria-pressed={index === selectedImage}
                >
                  <img
                    src={image}
                    srcSet={productImageSrcSet(image)}
                    sizes="48px"
                    alt=""
                    width="1200"
                    height="1200"
                    aria-hidden="true"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        <aside className="product-detail__buy-panel">
          <div
            className="product-detail__offer"
            itemProp="offers"
            itemScope
            itemType="https://schema.org/Offer"
          >
            <meta itemProp="priceCurrency" content={shipping.currency || "USD"} />
            <meta itemProp="price" content={currentPrice.toFixed(2)} />
            <link
              itemProp="availability"
              href={
                inStock
                  ? "https://schema.org/InStock"
                  : "https://schema.org/OutOfStock"
              }
            />

            <p className="product-detail__buy-label">Your selection</p>
            <div className="product-detail__price-row">
              <span className="product-detail__price">{money.format(currentPrice)}</span>
              {product.compareAtPrice && (
                <del>{money.format(Number(product.compareAtPrice))}</del>
              )}
            </div>
            <p className={`product-detail__stock ${inStock ? "is-in-stock" : "is-out-of-stock"}`}>
              <span aria-hidden="true" />
              {inStock
                ? hasManagedStock && stock <= 8
                  ? `Only ${stock} left in our pantry`
                  : "In stock and ready to pack"
                : "Currently out of stock"}
            </p>

            <div className="product-detail__quantity-block">
              <span id="product-quantity-label">Quantity</span>
              <div className="product-detail__quantity" aria-labelledby="product-quantity-label">
                <button
                  type="button"
                  onClick={() => updateQuantity(quantity - 1)}
                  disabled={quantity <= 1}
                  aria-label="Decrease quantity"
                >
                  <FiMinus aria-hidden="true" />
                </button>
                <output aria-live="polite">{quantity}</output>
                <button
                  type="button"
                  onClick={() => updateQuantity(quantity + 1)}
                  disabled={!inStock || quantity >= maxQuantity}
                  aria-label="Increase quantity"
                >
                  <FiPlus aria-hidden="true" />
                </button>
              </div>
            </div>

            <button
              className="product-detail__add-button"
              type="button"
              onClick={handleAddToCart}
              disabled={!inStock}
            >
              <FiShoppingBag aria-hidden="true" />
              {inStock ? `Add ${quantity} to cart` : "Out of stock"}
            </button>
            <button
              className={`product-detail__wish-button ${wished ? "is-active" : ""}`}
              type="button"
              aria-pressed={wished}
              onClick={() => toggleWishlist(product)}
            >
              <FiHeart aria-hidden="true" />
              {wished ? "Saved to favorites" : "Save to favorites"}
            </button>

            <div
              className={`product-detail__confirmation ${added ? "is-visible" : ""}`}
              role="status"
              aria-live="polite"
            >
              {added && (
                <>
                  <FiCheck aria-hidden="true" />
                  <span>Added to your cart.</span>
                  <Link to="/cartpage">View cart</Link>
                </>
              )}
            </div>

            <p className="product-detail__shipping-note">
              {shippingSummary(shipping)} Packed with care in{" "}
              {shipping.processingTime || "1–2 business days"}.
            </p>
          </div>
        </aside>
      </section>

      <section className="product-detail__story">
        <div className="product-detail__story-inner">
          <p className="product-detail__eyebrow">The story behind the jar</p>
          <h2>Good food begins with good ingredients.</h2>
          <p className="product-detail__story-intro">{product.description}</p>
          <p>{product.longDescription || product.description}</p>
        </div>
      </section>

      <section className="product-detail__reviews" aria-label="Customer reviews">
        <div className="product-detail__reviews-inner">
          <div className="product-detail__rating">
            <p className="product-detail__eyebrow">Catalog rating preview</p>
            <h2>{product.name} reviews</h2>
            <div className="product-detail__stars" aria-label={`${product.rating || 5} out of 5 stars`}>
              {Array.from({ length: 5 }, (_, index) => (
                <FiStar
                  key={index}
                  aria-hidden="true"
                  className={index < Math.round(product.rating || 5) ? "is-filled" : ""}
                />
              ))}
              <span>{Number(product.rating || 5).toFixed(1)}</span>
            </div>
            <p>{product.reviews || 0} sample ratings in this demo catalog</p>
          </div>
          <blockquote>
            Customer stories can appear here after a real review provider is
            connected. No review is submitted or verified by this front-end demo.
            <cite>Review integration preview</cite>
          </blockquote>
        </div>
      </section>

      <section className="product-detail__service">
        <div className="product-detail__service-inner">
          <div className="product-detail__service-heading">
            <p className="product-detail__eyebrow">Good to know</p>
            <h2>From our shelves to your table</h2>
          </div>
          <div className="product-detail__faq-list">
            <details open>
              <summary>How will my order ship?</summary>
              <p>
                We prepare orders in {shipping.processingTime || "1–2 business days"}.
                Standard delivery usually takes {shipping.standard?.estimatedBusinessDays || "3–5"} business days after dispatch.
              </p>
            </details>
            <details>
              <summary>How should I store it?</summary>
              <p>
                {product.attributes?.storage ||
                  "Keep sealed in a cool, dry pantry and follow the package directions after opening."}
              </p>
            </details>
            <details>
              <summary>What if something arrives damaged?</summary>
              <p>
                Keep the packaging and contact our customer care team within 48 hours.
                We will make it right as quickly as possible.
              </p>
            </details>
          </div>
        </div>
      </section>

      {relatedProducts.length > 0 && (
        <section className="product-detail__related">
          <div className="product-detail__related-heading">
            <div>
              <p className="product-detail__eyebrow">Complete your pantry</p>
              <h2>You may also like</h2>
            </div>
            <Link to="/shop">Shop all products</Link>
          </div>
          <div className="product-detail__related-grid">
            {relatedProducts.map((relatedProduct, index) => (
              <ProductCard
                key={relatedProduct.id}
                product={relatedProduct}
                tone={index % 2 === 0 ? "cream" : "sage"}
              />
            ))}
          </div>
        </section>
      )}
    </main>
  );
}

export default ProductDetail;
